/**
 * RGM Spotlight · Phase 2 — Admin A&R Review tab for a single
 * Spotlight cycle. Route: /admin/spotlight/:spotlightId/ar-review
 *
 * Reuses CriteriaEditor + JudgesTable pattern from Contest Judging
 * Phase 1. Aggregated results are decision-support signals only —
 * they NEVER automatically rank or pick winners. CEO uses the
 * existing Spotlight Winner Picker (in AdminDashboard) to finalize.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import CriteriaEditor from "@/components/contest/CriteriaEditor";

/**
 * Platform-level A&R Module master switch.
 * Distinct from the per-Spotlight "A&R Review: Enabled/Disabled for this
 * Spotlight" toggle rendered below. Flipping this control ONLY changes
 * `platform_settings.homepage.spotlight.ar_review.enabled`.
 *   • ON  → A&R routes stop being fail-closed globally. Individual
 *           Spotlight cycles remain in whatever state they were in;
 *           the CEO must still click Enable A&R per cycle to open it.
 *   • OFF → A&R routes return 404 spotlight_ar_module_disabled again.
 *           Existing rubrics, reviewers and reviews are preserved and
 *           re-appear when the master is flipped back ON.
 */
function PlatformARModuleSwitch({ onFlipped }) {
  const [state, setState] = React.useState({ loading: true, enabled: false });
  const load = React.useCallback(async () => {
    try {
      const r = await api.get("/homepage/config");
      const on = !!r.data?.spotlight?.ar_review?.enabled;
      setState({ loading: false, enabled: on });
    } catch {
      setState({ loading: false, enabled: false });
    }
  }, []);
  React.useEffect(() => { load(); }, [load]);
  const flip = async (target) => {
    if (!window.confirm(target
      ? "Turn the platform-level Spotlight A&R Module ON? This does NOT enable A&R on any Spotlight cycle — you must still click Enable A&R per cycle."
      : "Turn the platform-level Spotlight A&R Module OFF? All A&R endpoints will return 404 globally. Existing rubrics, reviewers and reviews are preserved and re-appear when you turn it back on.")) return;
    try {
      await api.put("/admin/homepage/spotlight-ar-toggle", { enabled: !!target });
      toast.success(target ? "Platform A&R Module: ON" : "Platform A&R Module: OFF");
      await load();
      onFlipped?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    }
  };
  return (
    <section className="max-w-3xl mx-auto mb-4" data-testid="admin-ar-platform-master-section">
      <div className="rounded border border-cyan-400/40 bg-cyan-500/[0.04] p-3">
        <div className="flex items-center justify-between gap-2">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-cyan-300 font-bold">Platform A&R Module</p>
            <p className="text-sm font-black text-white mt-0.5">
              {state.loading
                ? "…"
                : (state.enabled
                    ? <><span className="text-emerald-300">● ON</span> — Available platform-wide (per-cycle switch still required)</>
                    : <><span className="text-rose-300">● OFF</span> — A&R routes globally fail-closed (404)</>)}
            </p>
            <p className="text-[10px] text-gray-400 mt-1">
              Global availability of the A&R module.
              <strong className="mx-1 text-white">Not</strong>
              the same as the per-Spotlight <em>A&R Review Enabled/Disabled for this Spotlight</em> switch below.
              Turning this ON never enables A&R on any Spotlight cycle automatically.
            </p>
          </div>
          <button
            type="button"
            disabled={state.loading}
            onClick={() => flip(!state.enabled)}
            className={`px-3 py-2 rounded text-xs font-bold ${state.enabled
              ? "border border-amber-400/50 text-amber-200 hover:bg-amber-500/10"
              : "bg-cyan-600 text-white hover:bg-cyan-500"} disabled:opacity-40`}
            data-testid={state.enabled ? "admin-ar-platform-master-off" : "admin-ar-platform-master-on"}
          >
            {state.enabled ? "Turn Module OFF" : "Turn Module ON"}
          </button>
        </div>
      </div>
    </section>
  );
}

const _sortModes = {
  nominations: "🌟 Nominations",
  yes: "✅ YES count",
  avg: "📊 Avg rubric score",
  reviews: "🧾 Review count",
  alpha: "🔤 Alphabetical",
};

export default function AdminSpotlightARPage() {
  const { spotlightId } = useParams();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [meta, setMeta] = useState({});
  const [reviewers, setReviewers] = useState([]);
  const [results, setResults] = useState(null);
  const [q, setQ] = useState("");
  const [search, setSearch] = useState([]);
  const [saving, setSaving] = useState(false);
  const [criteriaDraft, setCriteriaDraft] = useState([]);
  const [requireScores, setRequireScores] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [sort, setSort] = useState("nominations");

  const reload = async () => {
    try {
      const c = await api.get(`/admin/spotlight/${spotlightId}/ar-review/config`);
      setConfig(c.data.config);
      setMeta({
        ar_review_closed_at: c.data.ar_review_closed_at,
        spotlight_status: c.data.spotlight_status,
        rubric_frozen: c.data.rubric_frozen,
      });
      setCriteriaDraft(c.data.config?.criteria || []);
      setRequireScores(!!c.data.config?.require_scores);
      setEnabled(!!c.data.ar_review_enabled);
      const j = await api.get(`/admin/spotlight/${spotlightId}/ar-reviewers`);
      setReviewers(j.data.reviewers || []);
      const rr = await api.get(`/admin/spotlight/${spotlightId}/ar-review/results`);
      setResults(rr.data);
    } catch (e) {
      if (e?.response?.status === 404) {
        toast.error("Spotlight A&R disabled — turn on in Control Center");
      } else {
        toast.error("Failed to load A&R config");
      }
    }
  };
  useEffect(() => { reload(); }, [spotlightId]); // eslint-disable-line

  useEffect(() => {
    if (!q || q.length < 2) { setSearch([]); return; }
    let cancel = false;
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/users/search", { params: { q, limit: 8 } });
        if (!cancel) setSearch(r.data?.users || []);
      } catch { if (!cancel) setSearch([]); }
    }, 300);
    return () => { cancel = true; clearTimeout(t); };
  }, [q]);

  const saveConfig = async (enableFlag) => {
    setSaving(true);
    try {
      await api.put(`/admin/spotlight/${spotlightId}/ar-review/config`, {
        criteria: criteriaDraft,
        scale: config?.scale || { min: 1, max: 10 },
        require_scores: requireScores,
        nominee_cap_per_reviewer: config?.nominee_cap_per_reviewer || 2,
        pool: config?.pool || [],
        ...(enableFlag == null ? {} : { enable: enableFlag }),
      });
      toast.success(enableFlag == null ? "Rubric saved" : (enableFlag ? "A&R review enabled" : "A&R review disabled"));
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const addReviewer = async (uid) => {
    try {
      await api.post(`/admin/spotlight/${spotlightId}/ar-reviewers`,
                     { reviewer_user_id: uid, assignment_scope: "all" });
      toast.success("Reviewer assigned"); setQ(""); setSearch([]); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Assign failed"); }
  };
  const removeReviewer = async (uid) => {
    if (!window.confirm("Remove this reviewer?")) return;
    try {
      await api.delete(`/admin/spotlight/${spotlightId}/ar-reviewers/${uid}`);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Remove failed"); }
  };
  const closeARReview = async () => {
    if (!window.confirm("Close A&R Review? Reviewers can no longer edit or submit after this. This does NOT publish the Spotlight.")) return;
    try {
      await api.post(`/admin/spotlight/${spotlightId}/ar-review/close`);
      toast.success("A&R review closed — decision-support panel is now read-only.");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Close failed"); }
  };

  // Phase 3 V1 — one-click "Start Private A&R Listening Room".
  // Wires the two already-shipped Phase 3 endpoints:
  //   1. POST /live/rooms            → creates a private_ar room with
  //                                    the logged-in admin as host.
  //   2. POST /live/rooms/{id}/ar/queue/load-spotlight
  //                                  → binds THIS Spotlight cycle's
  //                                    existing winners + review pool
  //                                    into the room's queue. The
  //                                    Spotlight itself is read-only.
  // On success → navigate the admin into the room. If either call
  // fails, surface the backend error verbatim and do NOT guess.
  const [startingARRoom, setStartingARRoom] = useState(false);
  const startARRoom = async () => {
    if (!spotlightId) {
      toast.error("No Spotlight cycle in context");
      return;
    }
    if (!window.confirm(
      "Start a Private A&R Listening Room for THIS Spotlight cycle?\n\n" +
      "• You become the host.\n" +
      "• The room's queue is loaded from this cycle's existing winners + review pool.\n" +
      "• The Spotlight cycle, scores, winners, rewards and publication state are NOT modified."
    )) return;
    setStartingARRoom(true);
    try {
      const created = await api.post("/live/rooms", {
        title: "Private A&R Listening Room — CEO QA",
        topic: `Spotlight ${meta.spotlight_status || ""}`.trim() || "A&R listening",
        room_type: "private_ar",
        allow_guests: false,
      });
      const roomId = created.data?.room?.room_id || created.data?.room_id;
      if (!roomId) throw new Error("Room creation returned no room_id");
      const bound = await api.post(`/live/rooms/${roomId}/ar/queue/load-spotlight`, {
        spotlight_id: spotlightId,
      });
      const count = bound.data?.count ?? 0;
      toast.success(`Room ready · ${count} track${count === 1 ? "" : "s"} queued`);
      navigate(`/beams/${roomId}`);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Failed to start A&R room";
      toast.error(String(detail));
    } finally {
      setStartingARRoom(false);
    }
  };

  const adopt = (submissionId) => {
    // Never writes winners. Simply hands the CEO back to the canonical
    // Spotlight Winner Picker with a hint in the URL for pre-fill.
    navigate(`/admin?prefill_spotlight_winner=${encodeURIComponent(submissionId)}`);
  };

  // Phase 1 — Eligible-submissions picker + Advance Next Weekly Cycle.
  // Never auto-writes to the pool; the CEO explicitly selects via
  // Select All / per-row toggles and clicks Save.
  const [eligOpen, setEligOpen] = useState(false);
  const [elig, setElig] = useState(null);        // { eligible: [], week_start, week_end, ... }
  const [eligBusy, setEligBusy] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [advancing, setAdvancing] = useState(false);

  const loadEligible = async () => {
    setEligBusy(true);
    try {
      const r = await api.get(`/admin/spotlight/${spotlightId}/eligible-submissions`);
      setElig(r.data);
      // Pre-seed selection from the cycle's existing pool so toggles
      // are additive, not destructive.
      const existingPool = new Set((config?.pool || []));
      const initial = new Set();
      (r.data?.eligible || []).forEach((row) => {
        if (existingPool.has(row.submission_id) || row.in_pool) initial.add(row.submission_id);
      });
      setSelected(initial);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load eligible submissions");
    } finally {
      setEligBusy(false);
    }
  };
  const openEligible = async () => {
    setEligOpen(true);
    await loadEligible();
  };
  const toggleOne = (sid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(sid)) next.delete(sid); else next.add(sid);
      return next;
    });
  };
  const selectAllEligible = () => {
    if (!elig?.eligible) return;
    setSelected(new Set(elig.eligible.map((r) => r.submission_id)));
  };
  const clearSelection = () => setSelected(new Set());
  const saveSelectedToPool = async () => {
    if (!config) return;
    setSaving(true);
    try {
      await api.put(`/admin/spotlight/${spotlightId}/ar-review/config`, {
        criteria: config.criteria || [],
        scale: config.scale || { min: 1, max: 10 },
        require_scores: !!config.require_scores,
        nominee_cap_per_reviewer: config.nominee_cap_per_reviewer || 2,
        pool: Array.from(selected),
      });
      toast.success(`Saved ${selected.size} track${selected.size === 1 ? "" : "s"} to A&R pool`);
      setEligOpen(false);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save pool");
    } finally {
      setSaving(false);
    }
  };

  const advanceNextCycle = async () => {
    if (!window.confirm(
      "Start the NEXT weekly Spotlight cycle?\n\n" +
      "• The current closed cycle (reviews, pool, ranking) stays unchanged.\n" +
      "• A new empty cycle is created with next-week boundaries.\n" +
      "• No winners, no pool, no rewards are carried over.\n" +
      "• You must enable A&R and pick a new pool on the new cycle."
    )) return;
    setAdvancing(true);
    try {
      const r = await api.post(`/admin/spotlight/${spotlightId}/advance-next`);
      const newId = r.data?.new_cycle?.spotlight_id;
      toast.success("Next weekly cycle created");
      if (newId) navigate(`/admin/spotlight/${newId}/ar-review`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Advance failed");
    } finally {
      setAdvancing(false);
    }
  };

  const reviewerNameMap = useMemo(() => {
    const m = {};
    (reviewers || []).forEach((rv) => {
      m[rv.reviewer_user_id] = rv.user?.artist_name || rv.user?.name || rv.user?.handle || rv.reviewer_user_id;
    });
    return m;
  }, [reviewers]);

  const canEditCriteria = !meta.rubric_frozen && !meta.ar_review_closed_at;

  const sortedRows = useMemo(() => {
    const rows = [...(results?.rows || [])];
    if (sort === "yes") rows.sort((a, b) => (b.yes_count - a.yes_count) || (b.nomination_count - a.nomination_count));
    else if (sort === "avg") rows.sort((a, b) => (b.avg - a.avg) || (b.yes_count - a.yes_count));
    else if (sort === "reviews") rows.sort((a, b) => b.review_count - a.review_count);
    else if (sort === "alpha") rows.sort((a, b) => (a.submission?.track_title || "").localeCompare(b.submission?.track_title || ""));
    else rows.sort((a, b) => (b.nomination_count - a.nomination_count) || (b.yes_count - a.yes_count) || (b.avg - a.avg));
    return rows;
  }, [results, sort]);

  return (
    <div className="min-h-screen bg-black text-white px-4 py-4" data-testid="admin-spotlight-ar-page">
      <header className="max-w-3xl mx-auto flex items-center gap-2 mb-4 flex-wrap">
        {/* Phase 1 — permanent deterministic exit back to RGM Studio
            Home. Admin still has ← Admin below, but ← Home guarantees
            an escape out of the Spotlight workflow at any time. */}
        <button
          onClick={() => navigate("/home")}
          className="text-gray-300 hover:text-white text-sm"
          data-testid="admin-spotlight-ar-home"
          aria-label="Exit Spotlight A&R and go to RGM Studio Home"
        >← Home</button>
        <button onClick={() => navigate("/admin")} className="text-fuchsia-300 text-sm" data-testid="admin-spotlight-ar-back">← Admin</button>
        <h1 className="text-lg font-black flex-1">🎧 Spotlight A&R Review</h1>
        {meta.ar_review_closed_at
          ? <span className="text-[10px] px-2 py-0.5 rounded bg-amber-500/20 text-amber-200" data-testid="admin-ar-closed-badge">Closed {new Date(meta.ar_review_closed_at).toLocaleString()}</span>
          : <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-200" data-testid="admin-ar-open-badge">A&R {enabled ? "OPEN" : "off (enable below)"}</span>}
        <button
          type="button"
          onClick={startARRoom}
          disabled={startingARRoom}
          className="px-3 py-1.5 rounded bg-fuchsia-600 text-white text-xs font-bold hover:bg-fuchsia-500 disabled:opacity-40"
          data-testid="admin-ar-start-private-room-btn"
          title="Create a Private A&R Listening Room, bind this Spotlight cycle's pool, and open it."
        >
          {startingARRoom ? "Starting…" : "🎧 Start Private A&R Room"}
        </button>
      </header>

      <PlatformARModuleSwitch onFlipped={reload} />

      <section className="max-w-3xl mx-auto space-y-4">
        <div className="rounded border border-white/10 bg-white/[0.02] p-3" data-testid="admin-ar-enable-section">
          <p className="text-[11px] text-gray-300">
            A&R Review is <strong>{enabled ? "enabled" : "disabled"}</strong> for this Spotlight cycle.
            Turn it on so assigned reviewers can score/nominate. Does NOT publish the Spotlight or pick winners.
          </p>
          <div className="mt-2 flex gap-2">
            {enabled
              ? <button onClick={() => saveConfig(false)} disabled={saving} className="px-3 py-1.5 rounded border border-amber-400/40 text-amber-200 text-xs font-bold hover:bg-amber-500/10" data-testid="admin-ar-disable-btn">Disable A&R</button>
              : <button onClick={() => saveConfig(true)} disabled={saving} className="px-3 py-1.5 rounded bg-fuchsia-600 text-xs font-bold hover:bg-fuchsia-500 disabled:opacity-30" data-testid="admin-ar-enable-btn">Enable A&R</button>}
          </div>
        </div>

        <div className="rounded border border-white/10 bg-white/[0.02] p-3" data-testid="admin-ar-criteria-section">
          <div className="flex items-center gap-2 mb-2">
            <p className="text-xs font-bold text-fuchsia-200">Rubric (optional — YES/MAYBE/NO is always required)</p>
            {meta.rubric_frozen && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-200" data-testid="admin-ar-rubric-frozen-badge">Rubric frozen (submitted review exists)</span>}
          </div>
          <CriteriaEditor
            criteria={criteriaDraft} scale={config?.scale || { min: 1, max: 10 }}
            disabled={!canEditCriteria} onChange={setCriteriaDraft}
          />
          <label className="mt-2 flex items-center gap-2 text-[11px] text-gray-300">
            <input type="checkbox" checked={requireScores} disabled={!canEditCriteria || criteriaDraft.length === 0}
                   onChange={(e) => setRequireScores(e.target.checked)}
                   className="accent-fuchsia-500" data-testid="admin-ar-require-scores" />
            Require scores when submitting (only relevant if criteria configured)
          </label>
          <div className="mt-2">
            <button onClick={() => saveConfig(null)} disabled={saving || !canEditCriteria}
                    className="px-3 py-1.5 rounded bg-fuchsia-600 text-xs font-bold hover:bg-fuchsia-500 disabled:opacity-30"
                    data-testid="admin-ar-criteria-save-btn">Save rubric</button>
          </div>
        </div>

        <div className="rounded border border-white/10 bg-white/[0.02] p-3" data-testid="admin-ar-reviewers-section">
          <p className="text-xs font-bold text-fuchsia-200 mb-2">A&R Reviewers</p>
          <input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search RGM artists by name / @handle / email"
                 className="w-full bg-black/40 border border-white/15 rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-fuchsia-400/70"
                 data-testid="admin-ar-reviewer-search"
                 disabled={!!meta.ar_review_closed_at} />
          {search.length > 0 && (
            <ul className="mt-2 space-y-1">
              {search.map((u) => (
                <li key={u.user_id}>
                  <button onClick={() => addReviewer(u.user_id)}
                          className="w-full text-left p-2 rounded bg-black/30 border border-white/10 hover:border-fuchsia-400/40 text-xs flex items-center gap-2"
                          data-testid={`admin-ar-reviewer-add-${u.user_id}`}>
                    <span className="flex-1">{u.artist_name || u.name || u.user_id}</span>
                    <span className="text-fuchsia-300 text-[10px]">Assign →</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-3">
            {reviewers.length === 0
              ? <p className="text-[11px] text-gray-500">No reviewers assigned yet.</p>
              : <ul className="space-y-1">
                  {reviewers.map((rv) => (
                    <li key={rv.reviewer_user_id} className="p-2 rounded border border-white/10 bg-black/30 flex items-center gap-2 text-xs" data-testid={`admin-ar-reviewer-row-${rv.reviewer_user_id}`}>
                      <span className="flex-1">{rv.user?.artist_name || rv.user?.name || rv.reviewer_user_id}</span>
                      <span className="text-[10px] tabular-nums text-gray-400">{rv.submitted}/{rv.assigned_count} · {rv.completion_pct}%</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${rv.status === "active" ? "bg-emerald-500/20 text-emerald-200" : "bg-gray-500/20 text-gray-300"}`}>{rv.status}</span>
                      {rv.status === "active" && !meta.ar_review_closed_at && (
                        <button onClick={() => removeReviewer(rv.reviewer_user_id)}
                                className="text-[10px] text-rose-300 hover:underline"
                                data-testid={`admin-ar-reviewer-remove-${rv.reviewer_user_id}`}>Remove</button>
                      )}
                    </li>
                  ))}
                </ul>}
          </div>
        </div>

        {!meta.ar_review_closed_at && (
          <button onClick={closeARReview}
                  className="w-full py-2 rounded border border-amber-500/50 text-amber-200 text-xs font-bold hover:bg-amber-500/10"
                  data-testid="admin-ar-close-btn">🔒 Close A&R Review (locks all reviews · does NOT publish Spotlight)</button>
        )}

        {/* Phase 1 — Weekly rollover. Only enabled after A&R review
            is closed on the current cycle. Creates a fresh empty
            cycle; never mutates winners, rewards or HoF. */}
        {meta.ar_review_closed_at && (
          <button
            onClick={advanceNextCycle}
            disabled={advancing}
            className="w-full py-2 rounded bg-emerald-600 hover:bg-emerald-500 text-xs font-bold disabled:opacity-40"
            data-testid="admin-ar-advance-next-btn"
            title="Create the next weekly Spotlight cycle. Current closed cycle is preserved immutable."
          >
            {advancing ? "Starting next cycle…" : "Start Next Weekly Cycle →"}
          </button>
        )}

        {/* Phase 1 — Eligible submissions picker for THIS cycle.
            Only shown when a cycle is OPEN (ar_review_closed_at is
            null); historical cycles stay read-only. */}
        {!meta.ar_review_closed_at && (
          <div className="rounded border border-white/10 bg-white/[0.02] p-3" data-testid="admin-ar-eligible-section">
            <div className="flex items-center gap-2 mb-2">
              <p className="text-xs font-bold text-fuchsia-200 flex-1">Eligible submissions this week</p>
              <button
                type="button"
                onClick={openEligible}
                className="px-2 py-1 rounded border border-fuchsia-400/40 text-fuchsia-200 text-[11px] font-bold hover:bg-fuchsia-500/10"
                data-testid="admin-ar-eligible-open-btn"
              >
                {eligOpen ? "Refresh" : "Open picker →"}
              </button>
            </div>
            {!eligOpen && (
              <p className="text-[10px] text-gray-500">
                Pick which eligible tracks become this cycle&apos;s A&R pool. Does not auto-dump all submissions.
              </p>
            )}
            {eligOpen && (
              <div data-testid="admin-ar-eligible-picker">
                {eligBusy && <p className="text-[11px] text-gray-400">Loading eligible submissions…</p>}
                {elig && (
                  <>
                    <div className="flex items-center gap-2 text-[10px] text-gray-300 mb-2 flex-wrap">
                      <span data-testid="admin-ar-eligible-count">Eligible this week: <strong className="text-white">{elig.eligible_count}</strong></span>
                      <span data-testid="admin-ar-eligible-selected">Selected for A&R: <strong className="text-fuchsia-200">{selected.size}</strong></span>
                      <span className="text-gray-500">Week {new Date(elig.week_start).toLocaleDateString()} → {new Date(elig.week_end).toLocaleDateString()}</span>
                      <button type="button" onClick={selectAllEligible}
                              className="ml-auto px-2 py-0.5 rounded border border-emerald-400/40 text-emerald-200 hover:bg-emerald-500/10"
                              data-testid="admin-ar-eligible-select-all">Select All Eligible</button>
                      <button type="button" onClick={clearSelection}
                              className="px-2 py-0.5 rounded border border-white/10 text-gray-300 hover:bg-white/5"
                              data-testid="admin-ar-eligible-clear">Clear</button>
                    </div>
                    {elig.eligible.length === 0 ? (
                      <p className="text-[11px] text-gray-500 p-2" data-testid="admin-ar-eligible-empty">
                        No submissions created inside this cycle&apos;s week window.
                      </p>
                    ) : (
                      <ul className="space-y-1 max-h-64 overflow-y-auto pr-1">
                        {elig.eligible.map((e_) => (
                          <li key={e_.submission_id}
                              className="p-2 rounded border border-white/10 bg-black/30 flex items-center gap-2"
                              data-testid={`admin-ar-eligible-row-${e_.submission_id}`}>
                            <input
                              type="checkbox"
                              checked={selected.has(e_.submission_id)}
                              onChange={() => toggleOne(e_.submission_id)}
                              className="accent-fuchsia-500"
                              data-testid={`admin-ar-eligible-toggle-${e_.submission_id}`}
                            />
                            {e_.cover_art
                              ? <img src={e_.cover_art} alt="" className="w-8 h-8 rounded object-cover border border-white/10" />
                              : <div className="w-8 h-8 rounded bg-black/40 border border-white/10" />}
                            <div className="flex-1 min-w-0">
                              <p className="text-[11px] font-bold text-white truncate">
                                {(e_.artist_name || "Unknown artist")} — {(e_.track_title || "Untitled track")}
                              </p>
                              <p className="text-[9px] text-gray-500">
                                {e_.created_at ? new Date(e_.created_at).toLocaleString() : ""} · {e_.status}
                              </p>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                    <div className="mt-2 flex gap-2">
                      <button
                        type="button"
                        onClick={saveSelectedToPool}
                        disabled={saving}
                        className="px-3 py-1.5 rounded bg-fuchsia-600 hover:bg-fuchsia-500 text-xs font-bold disabled:opacity-40"
                        data-testid="admin-ar-eligible-save-btn"
                      >
                        Save selected to A&R pool
                      </button>
                      <button
                        type="button"
                        onClick={() => setEligOpen(false)}
                        className="px-3 py-1.5 rounded border border-white/15 text-gray-300 text-xs"
                        data-testid="admin-ar-eligible-close-btn"
                      >
                        Cancel
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {results && (
          <div className="rounded border border-white/10 bg-white/[0.02] p-3" data-testid="admin-ar-results-section">
            {/* Phase 1 — A&R Judges' Current Final Ranking.
                Display-only decision support derived from the EXISTING
                current sort order of `sortedRows`. Never auto-writes
                winners or triggers rewards. */}
            {meta.ar_review_closed_at && sortedRows.length > 0 && (
              <div className="mb-3 rounded border border-fuchsia-400/40 bg-fuchsia-500/[0.06] p-2"
                   data-testid="admin-ar-ranked-top3">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-[11px] font-black text-fuchsia-100 flex-1">
                    A&R Judges&apos; Current Final Ranking
                  </p>
                  <span className="text-[9px] uppercase tracking-widest text-fuchsia-300">Decision support</span>
                </div>
                <ul className="space-y-1">
                  {sortedRows.slice(0, 3).map((r, idx) => {
                    const medal = idx === 0 ? "🥇" : idx === 1 ? "🥈" : "🥉";
                    const title = r.submission?.track_title || "Untitled track";
                    const artist = r.submission?.artist_name || "Unknown artist";
                    return (
                      <li key={r.submission_id}
                          className="flex items-center gap-2 p-1 rounded bg-black/30 border border-white/5"
                          data-testid={`admin-ar-ranked-${idx + 1}`}>
                        <span className="text-sm">{medal}</span>
                        {r.submission?.cover_art
                          ? <img src={r.submission.cover_art} alt="" className="w-8 h-8 rounded object-cover border border-white/10" />
                          : <div className="w-8 h-8 rounded bg-black/40 border border-white/10" />}
                        <div className="flex-1 min-w-0">
                          <p className="text-[11px] font-bold text-white truncate">
                            #{idx + 1} — {artist} — {title}
                          </p>
                          <p className="text-[9px] text-gray-400 tabular-nums">
                            🌟 {r.nomination_count} · ✅ {r.yes_count} · 📊 {r.avg} · 🧾 {r.review_count} review{r.review_count === 1 ? "" : "s"}
                          </p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
                <p className="text-[9px] text-amber-300 mt-1" data-testid="admin-ar-ranked-caveat">
                  A&R ranking recommendation — CEO confirmation required. Use the existing Spotlight Winner Picker to finalize.
                </p>
              </div>
            )}
            <div className="flex items-center gap-2 mb-2">
              <p className="text-xs font-bold text-fuchsia-200 flex-1">Decision-support signals ({results.pool_size} in pool)</p>
              <select value={sort} onChange={(e) => setSort(e.target.value)}
                      className="text-[10px] bg-black/40 border border-white/15 rounded px-1 py-0.5"
                      data-testid="admin-ar-sort-select">
                {Object.entries(_sortModes).map(([k, v]) => <option key={k} value={k}>Sort · {v}</option>)}
              </select>
            </div>
            <p className="text-[10px] text-gray-500 mb-2">These are signals to help you decide — RGM does not automatically pick winners from A&R data. Use the existing Spotlight Winner Picker in the Admin Dashboard to finalize.</p>
            <ul className="space-y-1">
              {sortedRows.length === 0 && <li className="text-[11px] text-gray-500 p-2">No submissions in review pool.</li>}
              {sortedRows.map((r) => {
                // Phase 1 — Post-close canonical identity. Keep the
                // submission_id as a diagnostic sub-line only when
                // artist/title are missing in the already-returned
                // backend payload (`r.submission`).
                const hasIdentity = meta.ar_review_closed_at
                  && (r.submission?.artist_name || r.submission?.track_title);
                const title = r.submission?.track_title || "";
                const artist = r.submission?.artist_name || "";
                return (
                <li key={r.submission_id} className="p-2 rounded border border-white/10 bg-black/30" data-testid={`admin-ar-result-row-${r.submission_id}`}>
                  <div className="flex items-center gap-2">
                    {hasIdentity && (r.submission?.cover_art
                      ? <img src={r.submission.cover_art} alt="" className="w-8 h-8 rounded object-cover border border-white/10"
                             data-testid={`admin-ar-result-cover-${r.submission_id}`} />
                      : <div className="w-8 h-8 rounded bg-black/40 border border-white/10" />)}
                    <div className="flex-1 min-w-0">
                      <span className="block truncate text-xs font-bold text-white"
                            data-testid={`admin-ar-result-identity-${r.submission_id}`}>
                        {hasIdentity
                          ? `${artist || "Unknown artist"} — ${title || "Untitled track"}`
                          : (title || r.submission_id.slice(0, 8))}
                      </span>
                      {hasIdentity && (
                        <span className="block text-[9px] text-gray-500 font-mono"
                              data-testid={`admin-ar-result-subid-${r.submission_id}`}>
                          id: {r.submission_id}
                        </span>
                      )}
                    </div>
                    <span className="text-[10px] tabular-nums text-emerald-300" title="YES votes">✅ {r.yes_count}</span>
                    <span className="text-[10px] tabular-nums text-amber-300" title="MAYBE votes">🤔 {r.maybe_count}</span>
                    <span className="text-[10px] tabular-nums text-rose-300" title="NO votes">❌ {r.no_count}</span>
                    <span className="text-[10px] tabular-nums text-fuchsia-300" title="Top-2 nominations">🌟 {r.nomination_count}</span>
                    {r.review_count > 0 && <span className="text-[10px] tabular-nums text-gray-300" title="Avg rubric score">📊 {r.avg}</span>}
                    <span className="text-[10px] tabular-nums text-gray-500">({r.review_count})</span>
                    <button onClick={() => adopt(r.submission_id)}
                            className="text-[10px] px-1.5 py-0.5 rounded border border-fuchsia-400/40 text-fuchsia-200 hover:bg-fuchsia-500/10"
                            data-testid={`admin-ar-adopt-${r.submission_id}`}>
                      Adopt in Winner Picker →
                    </button>
                  </div>
                  {r.per_reviewer.length > 0 && (
                    <details className="mt-1">
                      <summary className="text-[10px] text-gray-400 cursor-pointer">Reviewer details ({r.per_reviewer.length})</summary>
                      <ul className="mt-1 space-y-1 text-[10px]">
                        {r.per_reviewer.map((pr, idx) => (
                          <li key={idx} className="p-1 rounded bg-black/50 border border-white/5"
                              data-testid={`admin-ar-per-reviewer-${r.submission_id}-${idx}`}>
                            <div className="flex items-center gap-2">
                              <span className="flex-1 truncate text-gray-200">
                                {reviewerNameMap[pr.reviewer_user_id] || pr.reviewer_user_id}
                              </span>
                              <span className={pr.recommendation === "yes" ? "text-emerald-300" : pr.recommendation === "maybe" ? "text-amber-300" : "text-rose-300"}>{pr.recommendation?.toUpperCase()}</span>
                              {pr.is_nominee && <span className="text-fuchsia-300">🌟</span>}
                              {pr.average > 0 && <span className="text-gray-400 tabular-nums">avg {pr.average}</span>}
                            </div>
                            {pr.notes && <p className="text-gray-500 mt-0.5 whitespace-pre-wrap">&quot;{pr.notes}&quot;</p>}
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </li>
              );})}
            </ul>
            <p className="text-[10px] text-gray-500 mt-2">
              &quot;Adopt in Winner Picker →&quot; only pre-navigates to the existing Spotlight Winner Picker. It never writes winners, publishes, grants rewards or triggers HoF/notifications.
            </p>
          </div>
        )}
      </section>
    </div>
  );
}
